import { Injectable } from '@angular/core';
import { ILIFOCalc, IFinancialItem } from './reclassification.model';
import { Observable, Subject } from 'rxjs';
import { AUTO_CALC_FIELDS, READ_ONLY_HEADER_COLUMNS_MAPPING_ID } from './reclassification.const';

@Injectable({
  providedIn: 'root',
})
export class ReclassificationCalcService {
  private selectedDatabaseS: Subject<number> = new Subject<number>();
  public selectedDatabase$: Observable<number> = this.selectedDatabaseS.asObservable();

  public camelize(str) {
    return str
      .replace(/(?:^\w|^&|^\/[A-Z]|\b\w)/g, function (word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '');
  }

  public databaseChanged(dbId: number) {
    this.selectedDatabaseS.next(dbId);
  }

  calcLIFO(_LIFOReserveValues: any, _years: any[], source_name: string): ILIFOCalc[] {
    const retVal: ILIFOCalc[] = [];
    _years.forEach((y) => {
      const lrFieldName = source_name.toLowerCase() + '_' + y;
      const lrFieldNamePrevYear = source_name.toLowerCase() + '_' + (+y - 1);

      let adjValue = null;

      if (_LIFOReserveValues.storageName === 'Lifo' && _LIFOReserveValues[lrFieldNamePrevYear] === '0') {
        adjValue = this.nullIfZero(_LIFOReserveValues[lrFieldNamePrevYear]);
      } else {
        adjValue = this.nullIfNA(_LIFOReserveValues[lrFieldNamePrevYear]);
      }
      retVal.push({
        year: +y,
        lifoReserve: this.nullIfNA(_LIFOReserveValues[lrFieldName]),
        lifoAdj: this.substractIfNotNULL(this.nullIfNA(_LIFOReserveValues[lrFieldName]), adjValue),
        lifoAdjNegative:
          -1 * +this.substractIfNotNULL(this.nullIfNA(_LIFOReserveValues[lrFieldName]), this.nullIfNA(_LIFOReserveValues[lrFieldNamePrevYear])),
      });
    });

    return retVal;
  }

  nullIfNA(val: any) {
    return val == null ? null : +val;
  }

  substractIfNotNULL(v1: number, v2: number) {
    if (v1 == null) {
      return this.nullIfNA(v2);
    }
    if (v2 == null) {
      return this.nullIfNA(v1);
    }

    return +v1 - v2;
  }

  zeroIfNA(val: any): number {
    return val == null ? 0 : +val;
  }

  nullIfZero(val: any): number {
    return val == 0 ? null : +val;
  }

  substractIfNotZero(v1: number, v2: number) {
    if (v1 === 0) {
      return +v2;
    }
    if (v2 === 0) {
      return +v1;
    }

    return +v1 - v2;
  }

  addIfNotZero(v1: number, v2: number) {
    if (+v1 === 0) {
      return +v2;
    }
    if (+v2 === 0) {
      return +v1;
    }

    return (+v1 + v2).toFixed(2);
  }
  mapFinancialItemToPatchObject(fItems: IFinancialItem[]): any {
    const retVal: any = {};
    fItems.forEach((i) => {
      if (READ_ONLY_HEADER_COLUMNS_MAPPING_ID.indexOf(i.accountMappingId) === -1) {
        retVal[i.accountMappingId] = typeof +i.value === 'number' ? Number(+i.value).toFixed(2) : i.value;
      }
    });
    return retVal;
  }
  /* replace if not blank */
  calcReplacement(orgVal: any, newVal: any) {
    const result: any = {};
    Object.keys(orgVal).forEach((k) => {
      const val = newVal[k] != null ? +newVal[k] : +orgVal[k];
      result[k] = typeof val === 'number' ? Number(val).toFixed(2) : val;
    });
    return result;
  }

  addValues(orgVal: any, newVal: any) {
    const result: any = {};
    Object.keys(orgVal).forEach((k) => {
      const val = this.addIfNotZero(+orgVal[k], +newVal[k]);
      result[k] = typeof val === 'number' ? Number(val).toFixed(2) : val;
    });

    return result;
  }

  autoCalculations(orgVal: any, returnUpdatedOnly = false) {
    let result = {};

    if (!returnUpdatedOnly) {
      result = { ...orgVal };
    }

    const is = AUTO_CALC_FIELDS.incomeStatement;

    result[is.grossProfit] = (this.zeroIfNA(orgVal[is.operatingRevenue]) - this.zeroIfNA(orgVal[is.cogs])).toFixed(2);
    result[is.operatingIncome] = (this.zeroIfNA(result[is.grossProfit]) - this.zeroIfNA(orgVal[is.sga])).toFixed(2);

    const yeb = AUTO_CALC_FIELDS.yearEndBalance;

    result[yeb.totalCurrentAssets] = (
      this.zeroIfNA(orgVal[yeb.cashEquivalents]) +
      this.zeroIfNA(orgVal[yeb.totalAccountsReceivables]) +
      this.zeroIfNA(orgVal[yeb.totalInventories]) +
      this.zeroIfNA(orgVal[yeb.otherCurrentAssets])
    ).toFixed(2);

    result[yeb.totalAssets] = (
      this.zeroIfNA(result[yeb.totalCurrentAssets]) +
      this.zeroIfNA(orgVal[yeb.netProperty]) +
      this.zeroIfNA(orgVal[yeb.investments]) +
      this.zeroIfNA(orgVal[yeb.intangibes]) +
      this.zeroIfNA(orgVal[yeb.deferredAssets]) +
      this.zeroIfNA(orgVal[yeb.otherAssets])
    ).toFixed(2);

    const lia = AUTO_CALC_FIELDS.liabilities;
    result[lia.totalCurrentLiabilities] = (
      this.zeroIfNA(result[lia.accountPayable]) +
      this.zeroIfNA(orgVal[lia.currentInterest]) +
      this.zeroIfNA(orgVal[lia.currentOtherNonInterest])
    ).toFixed(2);

    result[lia.totalLiabilities] = (
      this.zeroIfNA(result[lia.totalCurrentLiabilities]) +
      this.zeroIfNA(orgVal[lia.totalLongTermDebt]) +
      this.zeroIfNA(orgVal[lia.deferredLiabilities]) +
      this.zeroIfNA(orgVal[lia.otherLiabilities])
    ).toFixed(2);

    result[lia.totalLiabilitiesAndStockholdersEquity] = (
      this.zeroIfNA(result[lia.totalLiabilities]) + this.zeroIfNA(orgVal[lia.totalStockHodersEquity])
    ).toFixed(2);

    return result;
  }

  private calcAverage(_currentYearValue: number, prevYearValue: number): number {
    const _curYearValueExists = _currentYearValue != null;
    const _prevYearValueExists = prevYearValue != null;

    if (_curYearValueExists && _prevYearValueExists) {
      return (+_currentYearValue + prevYearValue) / 2;
    }

    if (_curYearValueExists) {
      return +_currentYearValue;
    }

    if (_prevYearValueExists) {
      return +prevYearValue;
    }
    return 0;
  }
  public calculateAverages(adjustedFinanceCY: any, adjustedFinancePY: any, baseFinance: any, baseFinancePY: any): any {
    // no adjustments show base finance
    if (adjustedFinanceCY == null && adjustedFinancePY == null) {
      return null;
    }

    const currentYear = adjustedFinanceCY == null ? baseFinance : this.mapFinancialItemToPatchObject(adjustedFinanceCY);
    const prevYear = adjustedFinancePY == null ? baseFinancePY : this.mapFinancialItemToPatchObject(adjustedFinancePY);

    const result = {};

    Object.keys(currentYear).forEach((mappingID) => {
      result[mappingID] = this.calcAverage(currentYear[mappingID], prevYear[mappingID]);
    });

    return result;
  }
}
